import { useCallback, useState } from "react";

import { Button, Form } from "../lib";
import { Service, User, useUserServicesUpdate } from "../services";
import { useInit } from "../services/epox/utils";
import { ServiceInput } from "./service-input";

type Props = {
  services: Service[];
  user: User;
  onSuccess?: () => void;
};

export function ServicesForm({ services, user, onSuccess }: Props) {
  // Data
  const [updateUserServices, isUpdating] = useUserServicesUpdate();

  // State
  const [serviceIds, setServiceIds] = useState<number[]>([]);
  const [valueById, setValueById] = useState<Record<string, number>>({});

  // Handlers
  const handleToggle = useCallback(
    (id: string, active: boolean) => {
      const newServicesIds = serviceIds.filter((_id) => _id !== parseInt(id));

      if (active) {
        newServicesIds.push(parseInt(id));
      }

      setServiceIds(newServicesIds);
    },
    [serviceIds]
  );

  const handleChangeValue = useCallback(
    (id: string, value: number | null) => {
      const newValueById = { ...valueById };

      newValueById[id] = value ?? 0;

      setValueById(newValueById);
    },
    [valueById]
  );

  const handleSubmit = useCallback(async () => {
    const req = serviceIds.map((id) => ({ id, value: valueById[id] || 0 }));

    await updateUserServices(req);

    onSuccess?.();
  }, [onSuccess, serviceIds, updateUserServices, valueById]);

  // Effects
  useInit(() => {
    const initialServiceIds = user.services.map(({ id }) => id);
    const initialAmountsById = user.services.reduce(
      (acc, { id, value }) => ({ ...acc, [id]: value }),
      {}
    );

    setServiceIds(initialServiceIds);
    setValueById(initialAmountsById);
  });

  // Render
  return (
    <Form>
      {services.map(({ id, name, description, type }) => (
        <ServiceInput
          key={id}
          active={serviceIds.includes(id)}
          id={id}
          name={name}
          description={description}
          type={type}
          onChangeValue={handleChangeValue}
          onToggle={handleToggle}
          value={valueById[id] || 0}
        />
      ))}

      <Button em onPress={handleSubmit} loading={isUpdating}>
        Save changes
      </Button>
    </Form>
  );
}
